import { useCallback, useEffect, useRef } from 'react';

/**
 * 레이지 로딩 — 목록 끝에 둔 요소(sentinel)가 보이면 다음 페이지를 부른다.
 * 스크롤 컨테이너 안에 있어도 동작한다 (IntersectionObserver는 조상의 overflow 잘림을 반영한다).
 *
 * 사용: const sentinelRef = useLoadMoreSentinel({ hasNextPage, isFetchingNextPage, fetchNextPage });
 *       <div ref={sentinelRef} />
 */
export const useLoadMoreSentinel = ({
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  rootMargin = '240px',
}: {
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  /** 끝에 닿기 전에 미리 부를 거리 */
  rootMargin?: string;
}) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const nodeRef = useRef<Element | null>(null);
  // 최신 상태를 observer 콜백에서 읽기 위해 ref에 둔다
  const stateRef = useRef({ hasNextPage, isFetchingNextPage, fetchNextPage });
  stateRef.current = { hasNextPage, isFetchingNextPage, fetchNextPage };

  useEffect(() => () => observerRef.current?.disconnect(), []);

  // 한 페이지를 받은 뒤에도 sentinel이 여전히 보이면(화면이 덜 찼으면) 이어서 부르도록
  // 다시 관찰한다 — observe()는 시작할 때 현재 교차 상태로 콜백을 한 번 부른다
  useEffect(() => {
    const node = nodeRef.current;
    if (isFetchingNextPage || !hasNextPage || !node || !observerRef.current)
      return;
    observerRef.current.unobserve(node);
    observerRef.current.observe(node);
  }, [isFetchingNextPage, hasNextPage]);

  // callback ref — sentinel이 조건부로 그려져도 붙고 떨어질 때마다 다시 관찰한다
  return useCallback(
    (node: Element | null) => {
      observerRef.current?.disconnect();
      nodeRef.current = node;
      if (!node) return;
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const s = stateRef.current;
          if (
            entries.some((e) => e.isIntersecting) &&
            s.hasNextPage &&
            !s.isFetchingNextPage
          ) {
            void s.fetchNextPage();
          }
        },
        { rootMargin },
      );
      observerRef.current.observe(node);
    },
    [rootMargin],
  );
};
